/**
 * Onboarding data layer for the portal SPA. PRD-003 PR-06, PR-52, PR-07,
 * DATA-02, DATA-18; ARCH-001 (data layer), ARCH-002 (auth), ARCH-012 (the
 * signup-before-checkout flow this builds on).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * Entry condition (ARCH-012, the brief's "how a subscriber reaches onboarding")
 * ─────────────────────────────────────────────────────────────────────────────
 * Per ARCH-012 the prospect signs in via magic link BEFORE Stripe Checkout, so
 * by the time onboarding starts the subscriber is ALWAYS authenticated (has an
 * `auth.uid()` and a `subscribers` row). The Stripe webhook flips
 * `subscribers.status` to 'active' on payment. Onboarding is for a subscriber
 * whose `status='active'`; a `status='pending'` subscriber has not paid and
 * must not enter onboarding. The `/onboarding/<token>` URL token is a
 * routing / anti-enumeration convenience layered on top of the auth session —
 * it is NOT a substitute for auth. RLS still gates every read/write by
 * ownership of the signed-in session (`is_owner` / `auth.uid()`), which is why
 * the aldente "no auth required" phrasing predates ARCH-012 and no longer
 * holds: the corrected journeys.md §1.1 makes signup precede checkout.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * KNOWN DATA-PATH BLOCKER (surfaced to the orchestrator; no migration written)
 * ─────────────────────────────────────────────────────────────────────────────
 * Two writes the onboarding flow needs are NOT permitted to an authenticated
 * subscriber by the frozen RLS (migration 0007), and no SECURITY DEFINER RPC
 * for them exists yet:
 *
 *   1. context_docs INSERT is operator-only (`context_docs_operator_all`); the
 *      subscriber can only UPDATE an existing row. No context_docs row is
 *      created anywhere today (the ARCH-012 webhook is update-only and never
 *      touches onboarding tables; aldente journeys.md line 49 describes a
 *      creation that the frozen webhook does not implement). So step-1 autosave
 *      and final submit have no row to write into.
 *   2. notifications INSERT for an authenticated subscriber has no policy
 *      (`notifications_webhook_operator_insert` is `to anon` only;
 *      `notifications_operator_all` requires `is_operator()`). So the
 *      "notify Eric on submit" write (T-033 AC) is blocked.
 *
 * The clean fix is ONE additive SECURITY DEFINER RPC, callable by
 * `authenticated`, scoped to the caller's own subscriber via `auth.uid()`:
 *
 *   upsert_onboarding_context(p_sections jsonb, p_submit boolean)
 *     - upserts the caller's context_docs row (sections = p_sections,
 *       sections_schema_version = current)
 *     - if p_submit: sets onboarding_state.onboarding_status='submitted',
 *       submitted_at=now() for the caller, and inserts ONE notification of
 *       type 'onboarding_completed' addressed to operator_user_id()
 *     - else: sets onboarding_state.onboarding_status='in_progress'
 *     - resolves the subscriber by auth.uid() ONLY (no caller-supplied id),
 *       mirroring the create_pending_subscriber() posture (migration 0014)
 *
 * That is a new migration (0015). Per the impl v9 brief ("if you think you
 * need [a migration], STOP and report"), it is NOT authored here. This module
 * calls the RPC by its intended name so the flow is complete the moment the
 * migration lands; until then the RPC call returns a structured
 * `migration_pending` outcome and the UI shows an honest holding state rather
 * than silently dropping the subscriber's input.
 */

import {
  onboardingSectionsSchema,
  welcomeViewBlockToPlainText,
  type OnboardingSections,
  type OnboardingReview,
  type OnboardingQueueRow,
  contextDocSectionsPayloadSchema,
  JSONB_SCHEMA_VERSIONS,
} from '@studio/api-schemas';
import { supabase } from './supabase.js';
import { callOperatorEndpoint } from './operator.js';

/** Name of the not-yet-migrated SECURITY DEFINER RPC (migration 0015). */
const ONBOARDING_UPSERT_RPC = 'upsert_onboarding_context';

/**
 * Postgres error code for "function does not exist" (undefined_function).
 * Used to translate a missing-migration call into a graceful outcome rather
 * than a hard failure, so the flow degrades honestly until 0015 lands.
 */
const PG_UNDEFINED_FUNCTION = '42883';

export interface OnboardingContext {
  subscriberId: string;
  onboardingStateId: string;
  onboardingStatus: string;
  /** Sections already saved (resume support). */
  sections: OnboardingSections;
}

export type LoadOnboardingResult =
  | { kind: 'ok'; context: OnboardingContext }
  | { kind: 'not-found' } // bad/expired token, or not this subscriber's
  | { kind: 'not-active' } // subscriber has not paid (status != 'active')
  | { kind: 'unauthenticated' }
  | { kind: 'error'; message: string };

/**
 * Resolve the onboarding context for a token. RLS scopes every row to the
 * signed-in owner, so a token that is not the caller's resolves to not-found
 * (the surface never leaks another subscriber's onboarding).
 *
 * Entry guard: the subscriber must have `status='active'` (paid). A
 * `status='pending'` row (signed up, not yet checked out — ARCH-012) is
 * treated as not-active so the flow does not start before payment.
 */
export async function loadOnboardingByToken(
  token: string,
): Promise<LoadOnboardingResult> {
  const trimmed = token.trim();
  if (trimmed.length === 0) return { kind: 'not-found' };

  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData.session) return { kind: 'unauthenticated' };

  // onboarding_state is RLS-scoped to is_owner(subscriber_id); a token that is
  // not the caller's simply returns no row.
  const { data: stateRow, error: stateErr } = await supabase
    .from('onboarding_state')
    .select('id, subscriber_id, onboarding_status')
    .eq('onboarding_token', trimmed)
    .maybeSingle();

  if (stateErr) return { kind: 'error', message: stateErr.message };
  if (!stateRow) return { kind: 'not-found' };

  // Entry guard (ARCH-012): only an active (paid) subscriber onboards.
  const { data: sub, error: subErr } = await supabase
    .from('subscribers')
    .select('id, status')
    .eq('id', stateRow.subscriber_id)
    .maybeSingle();

  if (subErr) return { kind: 'error', message: subErr.message };
  if (!sub) return { kind: 'not-found' };
  if (sub.status !== 'active') return { kind: 'not-active' };

  // Resume support: read any partial sections already saved.
  const { data: doc } = await supabase
    .from('context_docs')
    .select('sections')
    .eq('subscriber_id', stateRow.subscriber_id)
    .maybeSingle();

  const parsedSections = onboardingSectionsSchema.safeParse(doc?.sections ?? {});

  return {
    kind: 'ok',
    context: {
      subscriberId: stateRow.subscriber_id,
      onboardingStateId: stateRow.id,
      onboardingStatus: stateRow.onboarding_status,
      sections: parsedSections.success ? parsedSections.data : {},
    },
  };
}

export type PersistOutcome =
  | { kind: 'saved' }
  | { kind: 'submitted' }
  | { kind: 'migration_pending' } // RPC (migration 0015) not deployed yet
  | { kind: 'error'; message: string };

/**
 * Autosave partial onboarding state (T-032 AC) or finalize the submission
 * (T-033 AC). Both go through the single SECURITY DEFINER RPC so the
 * subscriber never needs a context_docs INSERT grant or a notifications
 * INSERT grant (see the module header for why those are blocked at the RLS
 * layer and why this is an additive-migration candidate, not a workaround).
 */
export async function persistOnboarding(
  sections: OnboardingSections,
  options: { submit: boolean },
): Promise<PersistOutcome> {
  const cleaned = onboardingSectionsSchema.safeParse(sections);
  if (!cleaned.success) {
    return { kind: 'error', message: 'Onboarding answers failed validation.' };
  }

  const { error } = await supabase.rpc(ONBOARDING_UPSERT_RPC, {
    p_sections: cleaned.data,
    p_sections_schema_version: JSONB_SCHEMA_VERSIONS.contextDocSections,
    p_submit: options.submit,
  });

  if (error) {
    // The migration that ships the RPC (0015) is not deployed yet. Surface a
    // distinct outcome so the UI can hold honestly instead of pretending the
    // answers were saved.
    if (error.code === PG_UNDEFINED_FUNCTION) {
      return { kind: 'migration_pending' };
    }
    return { kind: 'error', message: error.message };
  }

  return options.submit ? { kind: 'submitted' } : { kind: 'saved' };
}

// ───────────────────────────────────────────────────────────────────────────
// Operator surface (T-035). The operator role has full RLS access via the
// *_operator_all policies (context_docs / onboarding_state / notifications),
// so the operator review action does NOT need the 0015 RPC and is fully
// functional today. Only the transactional email dispatch depends on the
// email provider (OQ-04 / T-047 ADR candidate, T-068 operator MCP) which is
// not built yet — that send is a documented integration seam below.
// ───────────────────────────────────────────────────────────────────────────

/**
 * List onboarding rows awaiting Eric's review (status='submitted'), joined
 * with the subscriber email and the context doc, for /eric/onboarding.
 * Operator-only; relies on the *_operator_all RLS policies.
 */
export async function listOnboardingQueue(): Promise<
  | { kind: 'ok'; rows: OnboardingQueueRow[] }
  | { kind: 'error'; message: string }
> {
  const { data, error } = await supabase
    .from('onboarding_state')
    .select(
      'id, subscriber_id, submitted_at, subscribers(email), context_docs:context_docs!context_docs_subscriber_id_fkey(sections)',
    )
    .eq('onboarding_status', 'submitted')
    .order('submitted_at', { ascending: true });

  if (error) {
    // The embedded-resource alias above depends on the FK name. Fall back to
    // separate reads if the join shape is rejected, so the queue still loads.
    return await listOnboardingQueueFallback();
  }

  const rows: OnboardingQueueRow[] = [];
  for (const r of data ?? []) {
    const row = r as Record<string, unknown>;
    const subscriber = pickFirst(row.subscribers) as { email?: string } | null;
    const ctx = pickFirst(row.context_docs) as { sections?: unknown } | null;
    const sections = contextDocSectionsPayloadSchema.safeParse(
      ctx?.sections ?? {},
    );
    rows.push({
      onboarding_state_id: String(row.id),
      subscriber_id: String(row.subscriber_id),
      subscriber_email: subscriber?.email ?? '(email unavailable)',
      submitted_at: (row.submitted_at as string | null) ?? null,
      sections: sections.success ? sections.data : {},
    });
  }
  return { kind: 'ok', rows };
}

async function listOnboardingQueueFallback(): Promise<
  | { kind: 'ok'; rows: OnboardingQueueRow[] }
  | { kind: 'error'; message: string }
> {
  const { data: states, error: statesErr } = await supabase
    .from('onboarding_state')
    .select('id, subscriber_id, submitted_at')
    .eq('onboarding_status', 'submitted')
    .order('submitted_at', { ascending: true });

  if (statesErr) return { kind: 'error', message: statesErr.message };

  const rows: OnboardingQueueRow[] = [];
  for (const s of states ?? []) {
    const { data: sub } = await supabase
      .from('subscribers')
      .select('email')
      .eq('id', s.subscriber_id)
      .maybeSingle();
    const { data: doc } = await supabase
      .from('context_docs')
      .select('sections')
      .eq('subscriber_id', s.subscriber_id)
      .maybeSingle();
    const sections = contextDocSectionsPayloadSchema.safeParse(
      doc?.sections ?? {},
    );
    rows.push({
      onboarding_state_id: s.id,
      subscriber_id: s.subscriber_id,
      subscriber_email: sub?.email ?? '(email unavailable)',
      submitted_at: s.submitted_at ?? null,
      sections: sections.success ? sections.data : {},
    });
  }
  return { kind: 'ok', rows };
}

export type ReviewOutcome =
  | { kind: 'reviewed'; emailDispatched: boolean }
  | { kind: 'error'; message: string };

/**
 * The single "Mark reviewed and send" action (T-035 AC). Operator-only.
 *
 * On one action:
 *   1. onboarding_state: eric_reviewed_at=now(), onboarding_status='eric_reviewed',
 *      welcome_view_block + welcome_email_body persisted.
 *   2. A notification of type 'welcome_view_ready' is inserted for the
 *      subscriber's auth.users id so the welcome view fires on next /portal
 *      entry (Phase G T-038 consumes this) and it shows in
 *      /portal/notifications (DES-12 footnote).
 *   3. The plain-text email is dispatched via the configured provider.
 *
 * Step 3's provider is OQ-04 / T-047 (an ADR candidate) and the operator MCP
 * tool surface is T-068 — neither is built. The send is therefore attempted
 * through `dispatchWelcomeEmail`, which currently records intent and reports
 * `emailDispatched=false` rather than fabricating a provider. The state +
 * notification writes (the parts that gate the welcome view) complete
 * regardless, so the subscriber-facing moment is not blocked on the email
 * provider decision.
 */
export async function markOnboardingReviewed(
  onboardingStateId: string,
  subscriberId: string,
  review: OnboardingReview,
): Promise<ReviewOutcome> {
  // 0. Endpoint-preferred path (T-068 server cycle). The shared service
  // (`@studio/studio-mcp` runMarkOnboardingReviewed) performs the same
  // writes the direct path below performs, AND inserts the
  // welcome_view_ready notification which the T-047 dispatcher cron picks
  // up to send the email. Falls back to the direct write on absent endpoint
  // (sandbox only).
  const endpoint = await callOperatorEndpoint<{
    welcome_view_notification_dispatched?: boolean;
    partial?: string;
  }>('/api/v1/operator/onboarding/mark-reviewed', {
    onboarding_state_id: onboardingStateId,
    subscriber_id: subscriberId,
    review,
  });
  if (endpoint.kind === 'ok') {
    // The dispatcher consumes the welcome_view_ready notification row the
    // service inserted; emailDispatched reflects the in-portal queue write,
    // not the email's transit. Surface the same boolean the legacy path used.
    return {
      kind: 'reviewed',
      emailDispatched: Boolean(endpoint.data.welcome_view_notification_dispatched),
    };
  }
  if (endpoint.kind === 'error') {
    return { kind: 'error', message: endpoint.message };
  }

  // Sandbox-only fallback (endpoint absent, kind === 'absent'). The operator
  // role has full RLS access via *_operator_all, so the writes below all
  // succeed; the email-dispatch leg falls back to its intent-only seam.

  // Resolve the subscriber's auth user id + email for the notification +
  // email. Operator RLS allows this read.
  const { data: sub, error: subErr } = await supabase
    .from('subscribers')
    .select('user_id, email')
    .eq('id', subscriberId)
    .maybeSingle();

  if (subErr) return { kind: 'error', message: subErr.message };
  if (!sub) return { kind: 'error', message: 'Subscriber not found.' };

  // 1. Ensure a context_docs row exists for the subscriber (operator may
  // INSERT via context_docs_operator_all). The onboarding submit RPC (0015)
  // normally creates it; on the operator side we make the surface resilient by
  // upserting an empty doc if submit happened before 0015 shipped.
  await supabase
    .from('context_docs')
    .upsert(
      {
        subscriber_id: subscriberId,
        sections: {},
        sections_schema_version: JSONB_SCHEMA_VERSIONS.contextDocSections,
      },
      { onConflict: 'subscriber_id', ignoreDuplicates: true },
    );

  // 2. Persist the review onto onboarding_state. welcome_view_block is stored
  // as PLAIN TEXT (welcomeViewBlockToPlainText) so Phase G's already-shipped
  // /portal welcome renderer (T-038), which reads the column as a bare string,
  // displays it correctly without Phase F touching Phase G's component. See
  // the welcomeViewBlockToPlainText doc for the cross-phase contract.
  const { error: stateErr } = await supabase
    .from('onboarding_state')
    .update({
      eric_reviewed_at: new Date().toISOString(),
      onboarding_status: 'eric_reviewed',
      welcome_view_block: welcomeViewBlockToPlainText(
        review.welcome_view_block,
      ),
      welcome_email_body: review.welcome_email_body,
    })
    .eq('id', onboardingStateId);

  if (stateErr) return { kind: 'error', message: stateErr.message };

  // 3. Notification so the welcome view fires on next /portal entry and is
  // saved in the notifications feed (DES-12). Operator RLS permits this INSERT.
  const { error: notifErr } = await supabase.from('notifications').insert({
    recipient_user_id: sub.user_id,
    subscriber_id: subscriberId,
    type: 'welcome_view_ready',
    payload: {
      welcome_view_block: review.welcome_view_block,
      onboarding_state_id: onboardingStateId,
      note: 'Eric has read your context. Welcome view ready on next portal entry.',
    },
  });

  if (notifErr) return { kind: 'error', message: notifErr.message };

  // 4. Email dispatch (sandbox seam: in production the T-068 endpoint above
  // routes through the T-047 dispatcher, so this leg only runs in a sandbox
  // without the endpoint registered).
  const emailDispatched = await dispatchWelcomeEmail(
    sub.email,
    review.welcome_email_body,
  );

  return { kind: 'reviewed', emailDispatched };
}

/**
 * Plain-text "Eric has read everything" email (DES-12a, GTM-10). The provider
 * is OQ-04 (Resend vs Postmark vs Supabase Auth's surface) — an ADR candidate
 * (T-047) — and the operator action MCP mirror is T-068. Neither exists, so
 * this records intent and returns false (not dispatched) rather than inventing
 * a provider. The welcome-view path does not depend on this returning true.
 */
async function dispatchWelcomeEmail(
  _to: string,
  _body: string,
): Promise<boolean> {
  // INTENTIONAL SEAM: real send lands with the OQ-04 provider decision (T-047
  // ADR) + the T-068 operator MCP tool. No fabricated provider here.
  return false;
}

function pickFirst(value: unknown): unknown {
  if (Array.isArray(value)) return value[0] ?? null;
  return value ?? null;
}
